import { MapViewerService, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { AfterViewInit, Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { Disclaimer } from '@models/types/components/Disclaimer.type';
import { DvmService } from '@services/dvm.service';
import { UiService } from '@services/ui.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import anime from 'animejs';

@Component({
    selector: 'app-disclaimer',
    imports: [],
    templateUrl: './disclaimer.component.html',
    styleUrl: './disclaimer.component.css'
})
export class DisclaimerComponent implements AfterViewInit {
  @ViewChild('disclaimerContainer')
  private readonly disclaimerContainer!: ElementRef<HTMLDivElement>;

  //SERVICES
  private readonly dvmService: DvmService = inject(DvmService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  //CONFIG
  private readonly uiconfig:    Signal<UiConfiguration> = inject(UiService).uiConfig;
  private readonly disclaimer:  Signal<Disclaimer>      = computed((): Disclaimer => this.uiconfig()['Disclaimer']);

  //STATE
  private readonly viewer: MapViewerService = this.dvmService.viewer;
  private readonly viewer3d: Viewer3dService = this.dvmService.viewer3d;

  private readonly view: Signal<AppViews | null> = this.dvmService.view;
  protected readonly showElement: WritableSignal<boolean> = signal(false);
  protected readonly disclaimerText: WritableSignal<string | null> = signal(null);
  private lastDisclaimer: Disclaimer | null = null;
  private activeDisclaimerKey: string | null = null;
  private activeDisclaimerDismissalToken: string | null = null;
  private dismissedDisclaimerToken: string | null = null;

  private previousView: AppViews | null = null;

  private onViewChange: EffectRef = effect(() => {
    const view = this.view();
    const disclaimer = this.disclaimer();

    if (!disclaimer || !view) return;

    // Some DVM transitions update the view signal after the SDK load_success event.
    // Re-evaluate on every view change so the disclaimer is shown when entering a pano,
    // not only when a later event or the exit from pano happens.
    const viewChanged = this.previousView !== view;

    // Detect disclaimer changes - effect re-runs any time this.disclaimer() is updated
    const disclaimerChanged = this.lastDisclaimer !== disclaimer;

    // Run handler when view or disclaimer data changes.
    if (viewChanged || disclaimerChanged) {
      this.handleDisclaimer(view);
    }

    this.lastDisclaimer = disclaimer;
  });

  ngAfterViewInit(): void {
    this.viewer
      .getObservable('load_success')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleDisclaimer(this.view()));
    this.viewer3d
      .getObservable('load_success')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleDisclaimer(this.view()));
  }

  private handleDisclaimer(view: AppViews | null): void {
    const disclaimer = this.disclaimer();

    // If disclaimer is globally disabled, hide disclaimer and exit
    if (!disclaimer.enabled) {
      this.toggleComponentVisibility(false);
      return;
    }

    const { allNodes } = disclaimer;
    const panoViewId = this.dvmService.currentView3d;
    const effectiveView = panoViewId ? AppViews.Pano : view;
    const isSeatmapView = effectiveView === AppViews.Seatmap;
    const isPanoView = effectiveView === AppViews.Pano;

    // Handle ALL NODES mode first
    const isAllNodesMode =
      allNodes.enabled &&
      (allNodes.allSeatmaps || allNodes.all3dViews) &&
      (isSeatmapView || isPanoView);

    if (isAllNodesMode) {
      this.handleAllNodesDisclaimer(effectiveView);
      this.saveState(effectiveView);
      return;
    }

    // Get current map and pano data, applying fallback for pano IDs (check seat, row + section)
    const currentMapKey = this.dvmService.currentMap!;

    const mapData = this.disclaimer().data[currentMapKey];
    const panoMatch = panoViewId
      ? this.getDisclaimerDataWithFallback(panoViewId)
      : null;

    // Determine which dataset applies
    let selectedData: { key: string; text: string } | null = null;
    if (isPanoView && panoMatch?.data.pano && panoMatch.data.text) {
      selectedData = { key: `pano:${panoMatch.key}`, text: panoMatch.data.text };
    } else if (isSeatmapView && mapData?.seatmap) {
      selectedData = { key: `seatmap:${currentMapKey}`, text: mapData.text };
    }

    // Show or hide the component
    const selectedDismissalToken = selectedData
      ? this.getDismissalToken(selectedData.key, selectedData.text)
      : null;

    if (selectedData?.text && selectedDismissalToken !== this.dismissedDisclaimerToken) {
      this.activeDisclaimerKey = selectedData.key;
      this.activeDisclaimerDismissalToken = selectedDismissalToken;
      this.toggleComponentVisibility(true);
      this.disclaimerText.set(this.formatText(selectedData.text));
    } else {
      if (!selectedData) {
        this.activeDisclaimerKey = null;
        this.activeDisclaimerDismissalToken = null;
        this.dismissedDisclaimerToken = null;
      }
      this.toggleComponentVisibility(false);
      if (!selectedData) this.disclaimerText.set(null);
    }

    // Save previous view for when we go from pano back to seatmap
    this.saveState(effectiveView);
  }

  private handleAllNodesDisclaimer(view: AppViews | null): void {
    const showDisclaimer =
      (view === AppViews.Seatmap && this.disclaimer().allNodes.allSeatmaps) ||
      (view === AppViews.Pano && this.disclaimer().allNodes.all3dViews);

    if (!showDisclaimer) {
      this.activeDisclaimerKey = null;
      this.activeDisclaimerDismissalToken = null;
      this.dismissedDisclaimerToken = null;
      this.toggleComponentVisibility(false);
      this.disclaimerText.set(null);
      return;
    }

    const selectedKey = `all-nodes:${view}`;
    const selectedDismissalToken = this.getDismissalToken(selectedKey, this.disclaimer().allNodes.text);
    if (selectedDismissalToken === this.dismissedDisclaimerToken) {
      this.toggleComponentVisibility(false);
      return;
    }

    this.activeDisclaimerKey = selectedKey;
    this.activeDisclaimerDismissalToken = selectedDismissalToken;
    this.toggleComponentVisibility(true);
    this.disclaimerText.set(this.formatText(this.disclaimer().allNodes.text));
  }

  private saveState(view: AppViews | null): void {
    this.previousView = view;
  }

  private formatText(text: string): string {
    return text.replace(/\\n|\n/g, '<br>');
  }

  private getDismissalToken(key: string, text: string): string {
    return `${key}:${text}`;
  }

  private getDisclaimerDataWithFallback(id: string) {
    const data = this.disclaimer().data;

    const parts = id.split('-');
    while (parts.length > 0) {
      const key = parts.join('-');
      if (data[key]) {
        return { key, data: data[key] };
      }
      parts.pop(); // remove last segment of ID to check seat, row, and then section in pano
    }
    return null;
  }

  close() {
    this.dismissedDisclaimerToken = this.activeDisclaimerDismissalToken;
    this.toggleComponentVisibility(false);
  }

  private toggleComponentVisibility(show: boolean): void {
    const disclaimerContainer: HTMLDivElement | undefined = this.disclaimerContainer?.nativeElement;
    if(!disclaimerContainer) return;

    anime.remove(disclaimerContainer);

    if (!this.showElement() && show) this.showElement.set(true);

    anime({
      targets: disclaimerContainer,
      opacity: show ? 1 : 0,
      easing: 'easeInOutQuad',
      duration: 300,
      complete: (): void => {
        if (!show) this.showElement.set(false);
      },
    });
  }
}
